import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Activity, Gauge, ListChecks, Layers } from 'lucide-react';
import { PageHeader } from '@/components/app/PageHeader';
import { LoadingState } from '@/components/app/LoadingState';
import { ErrorState } from '@/components/app/ErrorState';
import { EmptyState } from '@/components/app/EmptyState';
import { StatCard } from '@/components/app/StatCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { humanizeError } from '@/lib/apiClient';
import {
  funnelObservabilityApi,
  type PortfolioHealth,
} from '../api/funnelObservabilityApi';

const healthVariant: Record<PortfolioHealth, 'success' | 'warning' | 'destructive'> = {
  healthy: 'success',
  at_risk: 'warning',
  overloaded: 'destructive',
};

const statCardTone: Record<PortfolioHealth, 'success' | 'warning' | 'destructive'> = {
  healthy: 'success',
  at_risk: 'warning',
  overloaded: 'destructive',
};

export function PortfolioStatePage() {
  const [pendingUserId, setPendingUserId] = React.useState('');
  const [userId, setUserId] = React.useState('');

  const q = useQuery({
    queryKey: ['ai-portfolio-state', userId],
    queryFn: () => funnelObservabilityApi.getUserPortfolio(userId),
    enabled: !!userId,
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Portfolio state"
        description="What the Composer sees when scoring candidate approaches for a user."
      />

      <Card>
        <CardContent className="flex items-end gap-3 p-4">
          <div className="space-y-1.5 flex-1 max-w-md">
            <Label htmlFor="user-id">User ID</Label>
            <Input
              id="user-id"
              value={pendingUserId}
              onChange={(e) => setPendingUserId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && setUserId(pendingUserId.trim())}
              placeholder="Paste a userId..."
            />
          </div>
          <Button onClick={() => setUserId(pendingUserId.trim())}>Load</Button>
        </CardContent>
      </Card>

      {!userId ? (
        <EmptyState
          title="Enter a user ID"
          description="The portfolio is read-through — if missing, it is rebuilt from live phases."
        />
      ) : q.isLoading ? (
        <LoadingState />
      ) : q.isError || !q.data ? (
        <ErrorState
          title="Portfolio failed"
          description={humanizeError(q.error)}
        />
      ) : (
        <div className="space-y-4">
          <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatCard
              label="Health"
              value={q.data.portfolioHealth}
              icon={Gauge}
              tone={statCardTone[q.data.portfolioHealth]}
            />
            <StatCard
              label="Active phases"
              value={q.data.activePhases.length}
              icon={Layers}
            />
            <StatCard
              label="Weekly load"
              value={`${q.data.totalWeeklyLoad.toFixed(2)} / ${q.data.loadCeiling.toFixed(2)}`}
              icon={Activity}
              hint={`${Math.round(
                (q.data.totalWeeklyLoad / Math.max(q.data.loadCeiling, 0.01)) * 100,
              )}% of ceiling`}
            />
            <StatCard
              label="State version"
              value={q.data.stateVersion}
              icon={ListChecks}
              hint={`updated ${new Date(q.data.updatedAt).toLocaleDateString()}`}
            />
          </section>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Active phases ({q.data.activePhases.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {q.data.activePhases.length === 0 ? (
                <p className="px-4 py-6 text-sm text-muted-foreground">No active phases.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2">Phase</th>
                        <th className="px-3 py-2">Category</th>
                        <th className="px-3 py-2">Approach</th>
                        <th className="px-3 py-2">Commitment</th>
                        <th className="px-3 py-2 text-right">Load</th>
                        <th className="px-3 py-2 text-right">Days left</th>
                        <th className="px-3 py-2 text-right">Completion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {q.data.activePhases.map((p) => (
                        <tr key={p.phaseId} className="border-t">
                          <td className="px-3 py-2 font-mono text-xs">
                            {p.phaseId.slice(0, 10)}…
                          </td>
                          <td className="px-3 py-2">{p.categoryKey}</td>
                          <td className="px-3 py-2">{p.approachId}</td>
                          <td className="px-3 py-2">{p.commitmentLevel}</td>
                          <td className="px-3 py-2 text-right">
                            {p.weeklyLoadScore.toFixed(2)}
                          </td>
                          <td className="px-3 py-2 text-right">{p.daysRemaining}</td>
                          <td className="px-3 py-2 text-right">
                            {(p.completionRatioSoFar * 100).toFixed(0)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                Recent outcomes (7 days){' '}
                <Badge variant={healthVariant[q.data.portfolioHealth]}>
                  {q.data.portfolioHealth}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 text-sm">
              {q.data.recentOutcomes7d.length === 0 ? (
                <p className="text-muted-foreground">None yet.</p>
              ) : (
                q.data.recentOutcomes7d.map((o, i) => (
                  <div key={`${o.phaseId}-${i}`} className="flex items-center gap-2">
                    <Badge variant="muted" className="text-[10px]">
                      {o.event}
                    </Badge>
                    <code className="text-xs">{o.phaseId.slice(0, 10)}…</code>
                    <span className="text-xs text-muted-foreground">
                      {new Date(o.at).toLocaleString()}
                    </span>
                  </div>
                ))
              )}
              <p className="pt-2 text-xs text-muted-foreground">
                Last reconciled {new Date(q.data.lastReconciledAt).toLocaleString()}
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
